// Thin wrapper around fetch — every call uses credentials: 'include' so the
// HttpOnly session cookie travels.
//
// Why no CSRF token? The session cookie is SameSite=Strict, which prevents
// cross-site form submissions. SameSite=Strict + same-origin SPA + no
// public POST endpoints that mutate without a cookie = no CSRF surface
// for now. If we add a third-party embed or relax to Lax/None we'll need
// to revisit and add a double-submit token. Documented decision.

export type Role = "admin" | "operator" | "viewer";

export interface Me {
  user_id: string;
  email: string;
  display_name: string;
  role: Role;
}

export interface HostRow {
  id: string;
  fqdn: string | null;
  hostname: string;
  created_at: string;
  token_count: number;
}

export interface HostListResponse {
  items: HostRow[];
  total: number;
  page: number;
  size: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body?.detail ?? body?.title ?? detail;
    } catch {
      // ignore — keep statusText
    }
    throw new ApiError(resp.status, detail);
  }
  // 204 No Content — return undefined as T
  if (resp.status === 204) {
    return undefined as T;
  }
  return (await resp.json()) as T;
}

export const api = {
  login(email: string, password: string): Promise<{ status: string; session_id: string }> {
    return request("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  logout(): Promise<{ status: string }> {
    return request("/api/v1/auth/logout", { method: "POST" });
  },

  me(): Promise<Me> {
    return request<Me>("/api/v1/auth/me");
  },

  hosts(page = 1, size = 50): Promise<HostListResponse> {
    const qs = new URLSearchParams({ page: String(page), size: String(size) });
    return request<HostListResponse>(`/api/v1/hosts?${qs.toString()}`);
  },
};
